import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wand2, History, FileText, Bell, ArrowRight, Loader2, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { getAccessToken } from "@/lib/supabase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TOOLS = [
  { slug: "reply-assistant", icon: Wand2, title: "AI Reply Assistant", body: "Polish a one-word intent into a polished, human reply.", live: true, href: "/tools/reply-assistant" },
  { slug: "proposal-generator", icon: FileText, title: "Proposal Generator", body: "Turn a job post into a winning, personalized proposal.", live: true, href: "/tools/proposal-generator" },
  { slug: "client-analyzer", icon: Brain, title: "Client Analyzer", body: "Structured read of a job before you bid — skills, risk, strategy.", live: true, href: "/tools/client-analyzer" },
  { slug: "follow-up", icon: Bell, title: "Follow-up Generator", body: "Polite nudges that don't feel salesy.", live: false },
  { slug: "history", icon: History, title: "Conversation History", body: "Search your saved AI replies.", live: false },
];

export default function DashboardPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    document.title = "Dashboard — MACHA";
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/login?next=/dashboard", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const token = await getAccessToken();
      if (!token) return;
      try {
        const r = await fetch(`${BACKEND_URL}/api/generations?limit=5`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const data = await r.json();
          setRecent(data.items || []);
        }
      } catch { /* ignore */ }
    })();
  }, [user]);

  if (loading || !user) {
    return (
      <div className="flex min-h-[calc(100vh-3.5rem)] items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  const name = user.user_metadata?.full_name || user.email?.split("@")[0] || "there";

  return (
    <div className="mx-auto w-full max-w-6xl px-5 py-12 sm:px-8 sm:py-16">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Dashboard</p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            Welcome back, {name}.
          </h1>
          <p className="mt-2 text-[14px] text-muted-foreground">Pick a tool, or jump back into the Reply Assistant.</p>
        </div>
        <Button onClick={() => supabase.auth.signOut()} variant="outline" data-testid="signout-btn" className="rounded-lg">
          Sign out
        </Button>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2">
        {TOOLS.map(({ slug, icon: Icon, title, body, live, href }) => (
          <div key={slug} data-testid={`tool-${slug}`} className="flex flex-col gap-3 bg-card p-7">
            <div className="flex items-center justify-between">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground">
                <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
              </span>
              {live ? (
                <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-primary">Live</span>
              ) : (
                <span className="rounded-full border border-border bg-background px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">Soon</span>
              )}
            </div>
            <h3 className="text-[15px] font-semibold tracking-tight text-foreground">{title}</h3>
            <p className="text-[13px] leading-relaxed text-muted-foreground">{body}</p>
            {live ? (
              <Button asChild size="sm" className="mt-1 h-9 w-fit rounded-lg bg-primary text-[12.5px] text-primary-foreground hover:bg-primary/90">
                <Link to={href}>Open <ArrowRight className="ml-0.5 h-3.5 w-3.5" /></Link>
              </Button>
            ) : (
              <Button size="sm" variant="outline" disabled className="mt-1 h-9 w-fit rounded-lg text-[12.5px]">Coming soon</Button>
            )}
          </div>
        ))}
      </div>

      <div className="mt-12">
        <h2 className="text-[13px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">Recent replies</h2>
        <div className="mt-4 rounded-2xl border border-border bg-card">
          {recent.length === 0 ? (
            <p className="p-7 text-[13px] text-muted-foreground">No replies yet — try the Reply Assistant above.</p>
          ) : (
            <ul className="divide-y divide-border">
              {recent.map((g) => (
                <li key={g.id} data-testid={`history-row-${g.id}`} className="p-5">
                  <div className="flex items-center justify-between gap-3 text-[12px] text-muted-foreground">
                    <span className="font-mono uppercase tracking-wider">{g.tool_type}</span>
                    <span>{new Date(g.created_at).toLocaleString()}</span>
                  </div>
                  <p className="mt-2 line-clamp-3 text-[13.5px] text-foreground/90">{g.generated_response}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
